import React from 'react';
import { useField } from 'formik';
import { string, arrayOf, shape, oneOfType, bool, number } from 'prop-types';

const ButtonGroupSelect = ({
  name,
  label,
  options = [],
  placeholder = 'Select options',
  isClearable = true,
  className = '',
  classNameContainer = '',
  disabled = false,
  ...props
}) => {
  const [field, meta, helpers] = useField(name);

  const selectedValues = field.value || [];

  const handleOptionToggle = (optionValue) => {
    if (disabled) return;

    const newValues = selectedValues.includes(optionValue)
      ? selectedValues.filter((value) => value !== optionValue)
      : [...selectedValues, optionValue];

    helpers.setValue(newValues);
  };

  const handleClear = () => {
    if (disabled) return;
    helpers.setValue([]);
  };

  const hasError = meta.touched && meta.error;

  return (
    <div className={`tw-flex tw-flex-col tw-gap-2 ${classNameContainer}`}>
      <div className='tw-flex tw-justify-between tw-items-center'>
        {label && (
          <label className='tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300'>
            {label}
          </label>
        )}
        {isClearable && selectedValues.length > 0 && (
          <button
            type='button'
            onClick={handleClear}
            disabled={disabled}
            className='tw-text-xs tw-text-gray-500 hover:tw-text-gray-700 dark:tw-text-gray-400 dark:hover:tw-text-gray-300 tw-transition-colors tw-duration-200'
          >
            Clear selection
          </button>
        )}
      </div>

      <div className={`tw-flex tw-flex-wrap tw-gap-2 ${className}`}>
        {options.map((option) => {
          const isSelected = selectedValues.includes(option.value);
          const isDisabled = disabled || option.disabled;

          return (
            <button
              key={option.value}
              type='button'
              onClick={() => handleOptionToggle(option.value)}
              disabled={isDisabled}
              className={`
                tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-rounded-full tw-border tw-transition-all
                tw-duration-200 tw-ease-in-out tw-cursor-pointer tw-min-w-[80px] tw-text-center
                ${
                  isSelected
                    ? 'tw-border-blue-500 tw-bg-white tw-text-blue-600 dark:tw-border-blue-400 dark:tw-bg-gray-800 dark:tw-text-blue-400 tw-shadow-sm'
                    : 'tw-border-gray-300 tw-bg-white tw-text-gray-700 hover:tw-border-gray-400 dark:tw-border-gray-600 dark:tw-bg-gray-800 dark:tw-text-gray-300 dark:hover:tw-border-gray-500'
                }
                ${isDisabled ? 'tw-opacity-50 tw-cursor-not-allowed' : 'hover:tw-shadow-sm'}
              `}
            >
              {option.label}
            </button>
          );
        })}
      </div>

      {hasError && (
        <div className='tw-text-xs tw-text-red-600 dark:tw-text-red-400'>{meta.error}</div>
      )}
    </div>
  );
};

ButtonGroupSelect.propTypes = {
  name: string.isRequired,
  label: string,
  options: arrayOf(
    shape({
      value: oneOfType([string, number]).isRequired,
      label: string.isRequired,
      disabled: bool,
    })
  ),
  placeholder: string,
  isClearable: bool,
  className: string,
  classNameContainer: string,
  disabled: bool
};

ButtonGroupSelect.defaultProps = {
  options: [],
  placeholder: 'Select options',
  isClearable: true,
  className: '',
  classNameContainer: '',
  disabled: false,
};

export default ButtonGroupSelect;
